// D9 (DISCREPANCIES-V2 §2, the P4 amendment): three truthful non-live states,
// no false "expired". Pure mapping so the rule is test-pinned without a
// renderer; App.tsx consumes it on both paths a refusal can arrive by —
// the resolve (before permissions) and the join (a race can outdate the
// resolve at any moment).

import type { ErrorCode, ResolveLinkResponse } from '@advaitnetra/shared';

export type NotLiveStatus = 'pending' | 'ended' | 'expired';

/**
 * Map a non-joinable resolve to the screen the patient should see.
 *
 * The server's `reason` is authoritative — it is linkState's own verdict, so
 * ended orders ahead of expired (a finished call never reads as broken) and
 * an expired-but-pending link says "expired", never "not started yet".
 */
export function notLiveStatusFromResolve(
  resolved: Pick<ResolveLinkResponse, 'status'> & Partial<Pick<ResolveLinkResponse, 'reason'>>,
): NotLiveStatus {
  switch (resolved.reason) {
    case 'link_expired':
      return 'expired';
    case 'session_ended':
      return 'ended';
    case 'session_not_live':
      return 'pending';
    default:
      // A server predating the `reason` field: fall back to the pre-D9 status
      // map (ended/dropped read as over, everything else waits) rather than
      // claiming an expiry nothing asserted.
      return resolved.status === 'ended' || resolved.status === 'dropped' ? 'ended' : 'pending';
  }
}

/**
 * Map a join refusal to the same three states. Codes that name a link state
 * render as that state; anything else returns null and stays a generic error.
 */
export function notLiveStatusFromJoinFailure(code: ErrorCode): NotLiveStatus | null {
  switch (code) {
    case 'link_expired':
      return 'expired';
    case 'session_ended':
      return 'ended';
    case 'session_not_live':
      return 'pending';
    default:
      return null;
  }
}
